import * as React from "react";
import cx from "classnames";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from "../field-heading.utils";

import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { TableFieldProps, useTableFieldController } from "./table-field.controller";
import { useFieldContentStyle } from "../content-style.utils";
import { useForceDisabled } from '../../context';
import { HeaderColumn } from "./components/column";
import { FieldContainer } from "../field-container";
import { Button } from "@common/components/Button";
import { Row } from "./components/row";
import { MessageBox } from "@common/components/MessageBox";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { getTableReportFormats } from "@modules/datasheets/services/datasheet";

import "./table-field.styles.scss";

type TableColumnCandidate = {
  field: DatasheetFieldComponentOriginFieldGetDto;
  width: number;
  order: number | undefined;
};

type UnknownRecord = Record<string, unknown>;

type TableSubfieldSource = UnknownRecord & {
  field?: UnknownRecord;
  fieldid?: unknown;
  width?: unknown;
  fontname?: unknown;
  fontName?: unknown;
  styleFontName?: unknown;
  fontsize?: unknown;
  fontSize?: unknown;
  styleFontSize?: unknown;
  fontcolor?: unknown;
  fontColor?: unknown;
  textstyle?: unknown;
  textStyle?: unknown;
  contentalign?: unknown;
  contentAlign?: unknown;
  order?: unknown;
  columnOrder?: unknown;
  reportOrder?: unknown;
  displayOrder?: unknown;
};

const toPositiveOrder = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const getNumberValue = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const getFieldIdValue = (value: unknown): number | undefined => {
  return getNumberValue(value);
};

const getOrderValue = (subfield: TableSubfieldSource): unknown => {
  return (
    subfield?.order ??
    subfield?.columnOrder ??
    subfield?.reportOrder ??
    subfield?.displayOrder ??
    subfield?.field?.order ??
    subfield?.field?.columnOrder ??
    subfield?.field?.reportOrder ??
    subfield?.field?.displayOrder
  );
};

const getOriginFieldFromSource = (
  subfield: TableSubfieldSource,
  fallbackOriginSubfields: Array<DatasheetFieldComponentOriginFieldGetDto>
): DatasheetFieldComponentOriginFieldGetDto | null => {
  const sourceField = subfield.field;
  const sourceFieldId = getFieldIdValue(sourceField?.fieldid);
  const subfieldId = getFieldIdValue(subfield.fieldid);
  if (sourceFieldId != null) {
    const matched = fallbackOriginSubfields.find((field) => field.fieldid === sourceFieldId);
    if (matched) {
      return matched;
    }
  }
  if (subfieldId != null) {
    const matched = fallbackOriginSubfields.find((field) => field.fieldid === subfieldId);
    if (matched) {
      return matched;
    }
  }
  if (sourceFieldId != null && sourceField && typeof sourceField.fieldname === "string") {
    return sourceField as unknown as DatasheetFieldComponentOriginFieldGetDto;
  }
  return null;
};

const buildCandidatesFromSource = (
  source: Array<TableSubfieldSource>,
  fallbackOriginSubfields: Array<DatasheetFieldComponentOriginFieldGetDto>
): { candidates: Array<TableColumnCandidate>; hasExplicitOrder: boolean } => {
  const candidates: Array<TableColumnCandidate> = [];
  source.forEach((subfield) => {
    const originField = getOriginFieldFromSource(subfield, fallbackOriginSubfields);

    if (!originField) {
      return;
    }

    const originAny = originField as Record<string, unknown>;
    const mergedField = {
      ...originField,
      fontname:
        subfield.fontname ??
        subfield.fontName ??
        subfield.styleFontName ??
        originAny.fontname,
      fontsize:
        subfield.fontsize ??
        subfield.fontSize ??
        subfield.styleFontSize ??
        originAny.fontsize,
      fontcolor: typeof subfield.fontcolor === "number"
        ? subfield.fontcolor
        : (typeof subfield.fontColor === "number" ? subfield.fontColor : originAny.fontcolor),
      textstyle: subfield.textstyle ?? subfield.textStyle ?? originAny.textstyle,
      contentalign: subfield.contentalign ?? subfield.contentAlign ?? originAny.contentalign,
    } as DatasheetFieldComponentOriginFieldGetDto;

    candidates.push({
      field: mergedField,
      width: getNumberValue(subfield.width) ?? originField.defaultWidth ?? 100,
      order: toPositiveOrder(getOrderValue(subfield)),
    });
  });

  const hasExplicitOrder = source.some((subfield) => getOrderValue(subfield) != null);
  return { candidates, hasExplicitOrder };
};

export const TableField: React.FC<TableFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;
  const { formControlStyle } = useFieldContentStyle(datasheetElement.original);
  const forceDisabled = useForceDisabled();
  const [defaultReportRowsSource, setDefaultReportRowsSource] = React.useState<Array<TableSubfieldSource> | undefined>(undefined);

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Table columns can come from different DTO shapes depending on API/mapper:
  // - Newer shape: `datasheetElement.original.field.subfields` (origin-field DTOs)
  // - Legacy shape: `datasheetElement.original.m_ds_subfields` or `dssubfields` (field-component DTOs)
  const {
    selectedItemId,
    currentValue,
    footerActions,
    headingReference,
    headingPlacedHorizontally,
    headingSize,
    headingPosition,
    handleResizeColumn,
    handleRowClick,
    handleCellValueChange,
    messageProps,
    closeMessage,
    isEditable,
  } = useTableFieldController(props);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  React.useEffect(() => {
    const context = props.context;
    const dsFormatId = props.parentFormatId;
    const tabId = props.parentTabId;
    const tableFieldId = datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id;
    if (
      context == null ||
      dsFormatId == null ||
      dsFormatId <= 0 ||
      tabId == null ||
      tabId <= 0 ||
      tableFieldId == null ||
      tableFieldId <= 0
    ) {
      setDefaultReportRowsSource(undefined);
      return;
    }

    let cancelled = false;
    getTableReportFormats({
      context,
      dsFormatId,
      tabId,
      tableFieldId,
    })
      .then((formats) => {
        if (cancelled) {
          return;
        }
        const selected = formats.find((format) => format.isDefault);
        if (!selected || !Array.isArray(selected.rows)) {
          setDefaultReportRowsSource(undefined);
          return;
        }
        const rowsSource = selected.rows.map((row) => ({
          field: {
            fieldid: row.subfield.fieldId,
            fieldname: row.subfield.fieldName,
            imageURL: row.subfield.imageURL,
            defaultWidth: row.width ?? 100,
          },
          fieldid: row.subfield.fieldId,
          width: row.width,
          order: row.order,
        }));
        setDefaultReportRowsSource(rowsSource);
      })
      .catch(() => {
        if (!cancelled) {
          setDefaultReportRowsSource(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    props.context,
    props.parentFormatId,
    props.parentTabId,
    datasheetElement.original.field.fieldid,
    datasheetElement.original.field.id,
  ]);

  const resolvedColumns = React.useMemo(() => {
    const tableReportFormat = (datasheetElement.original as { tableReportFormat?: string | null }).tableReportFormat;
    const fallbackOriginSubfields = datasheetElement.original.field.subfields || [];
    if (tableReportFormat == null) {
      const hasExplicitDsSubfields = Object.prototype.hasOwnProperty.call(datasheetElement.original, "dssubfields");
      const explicitDsSubfields = datasheetElement.original.dssubfields;
      const legacyDsSubfields = datasheetElement.original.m_ds_subfields;
      const configuredSubfields = hasExplicitDsSubfields
        ? explicitDsSubfields
        : (Array.isArray(legacyDsSubfields) && legacyDsSubfields.length > 0 ? legacyDsSubfields : undefined);

      if (Array.isArray(configuredSubfields)) {
        if (configuredSubfields.length === 0) {
          return [];
        }
        const { candidates, hasExplicitOrder } = buildCandidatesFromSource(configuredSubfields as unknown as Array<TableSubfieldSource>, fallbackOriginSubfields);
        if (hasExplicitOrder) {
          return candidates
            .filter((candidate) => typeof candidate.order === "number" && candidate.order > 0)
            .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
            .map(({ field, width }) => ({ field, width }));
        }
        if (!hasExplicitOrder && Array.isArray(defaultReportRowsSource) && defaultReportRowsSource.length > 0) {
          const defaultResult = buildCandidatesFromSource(defaultReportRowsSource, fallbackOriginSubfields);
          if (defaultResult.hasExplicitOrder) {
            return defaultResult.candidates
              .filter((c) => typeof c.order === "number" && c.order > 0)
              .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
              .map(({ field, width }) => ({ field, width }));
          }
        }
        return candidates.map(({ field, width }) => ({ field, width }));
      }
      if (Array.isArray(defaultReportRowsSource)) {
        if (defaultReportRowsSource.length === 0) {
          return [];
        }
        const { candidates, hasExplicitOrder } = buildCandidatesFromSource(defaultReportRowsSource, fallbackOriginSubfields);
        if (hasExplicitOrder) {
          return candidates
            .filter((candidate) => typeof candidate.order === "number" && candidate.order > 0)
            .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
            .map(({ field, width }) => ({ field, width }));
        }
        if (candidates.length > 0) {
          return candidates.map(({ field, width }) => ({ field, width }));
        }
      }

      return fallbackOriginSubfields.map((field) => ({
        field,
        width: field.defaultWidth || 100,
      }));
    }

    const sources: Array<Array<TableSubfieldSource> | undefined> = [
      defaultReportRowsSource,
      datasheetElement.original.dssubfields as unknown as Array<TableSubfieldSource> | undefined,
      datasheetElement.original.m_ds_subfields as unknown as Array<TableSubfieldSource> | undefined,
      datasheetElement.original.subfields as Array<TableSubfieldSource> | undefined,
    ];

    for (const source of sources) {
      if (!Array.isArray(source)) {
        continue;
      }
      if (source.length === 0) {
        return [];
      }

      const { candidates, hasExplicitOrder } = buildCandidatesFromSource(source, fallbackOriginSubfields);

      if (hasExplicitOrder) {
        return candidates
          .filter((candidate) => typeof candidate.order === "number" && candidate.order > 0)
          .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
          .map(({ field, width }) => ({ field, width }));
      }

      if (candidates.length > 0) {
        return candidates.map(({ field, width }) => ({ field, width }));
      }
    }

    return fallbackOriginSubfields.map((field) => ({
      field,
      width: field.defaultWidth || 100,
    }));
  }, [datasheetElement.original.dssubfields, datasheetElement.original.m_ds_subfields, datasheetElement.original.subfields, datasheetElement.original.field.subfields, (datasheetElement.original as { tableReportFormat?: string | null }).tableReportFormat, defaultReportRowsSource]);

  return (
    <DatasheetItem
      className="DatasheetsResultDatasheetTableField"
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      {labelComponent}
      <Flex style={{ position: 'relative' }}>
        <FieldContainer
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, alignItems: 'stretch' }}
        >
          <div 
            className={cx(
              "DatasheetsResultDatasheetTableField__table", 
            )}
          >
            <Flex className="DatasheetsResultDatasheetTableField__table__header" reference={containerRef}>
              {resolvedColumns.map((column) => (
                <HeaderColumn
                  width={column.width}
                  onResizeEnd={handleResizeColumn}
                  title={column.field.fieldname}
                  containerRef={containerRef}
                  key={column.field.fieldid}
                  field={column.field}
                />
              ))}
            </Flex>
            <div className="DatasheetsResultDatasheetTableField__table__body">
              {!forceDisabled && currentValue?.map((dataItem) => (
                <Row 
                  selected={selectedItemId === dataItem.id}
                  onClick={handleRowClick}
                  onCellChange={handleCellValueChange}
                  key={dataItem.id}
                  columns={resolvedColumns}
                  data={dataItem}
                  editable={isEditable}
                  tableContentStyle={formControlStyle}
                />
              ))}
            </div>
            <Flex className="DatasheetsResultDatasheetTableField__table__footer">
              {footerActions.map((action) => {
                const actionDisabled = forceDisabled || action.disabled;
                return (
                  <Button
                    disabled={actionDisabled}
                    style={{ marginRight: 8 }}
                    variant="text"
                    key={action.label}
                    text={
                      <>
                        <img
                          className="DatasheetsResultDatasheetTableField__footerIcon"
                          src={actionDisabled ? action.icon.disabled : action.icon.enabled}
                        />
                        <span className="DatasheetsResultDatasheetTableField__footerLabel">{action.label}</span>
                      </>
                    }
                    onClick={(e) => {
                      // Ensure button clicks don't propagate to parent elements
                      e.stopPropagation();
                      if (!actionDisabled && action.onClick) {
                        action.onClick(e);
                      }
                    }}
                  />
                );
              })}
            </Flex>
          </div>
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});
